import {
  normalizeSharedConsentSnapshot,
  sharedConsentSnapshotsEqual,
} from "./shared-consent";
import type { StorageAdapter } from "./storage";
import type { TransportResult } from "./transport";
import type { ConsentSnapshot } from "./types";

export type ConsentEvidencePayload = {
  state: "granted" | "denied";
  gpc: boolean;
  dnt: boolean;
  cmp?: ConsentSnapshot["cmp"];
  signalSource: string;
};

export type ConsentEvidence = {
  fingerprint: string;
  payload: ConsentEvidencePayload;
};

export type ConsentEvidenceOutboxMarker = {
  fingerprint: string;
  revisionToken: string | null;
};

type ConsentEvidenceAcknowledgement = ConsentEvidenceOutboxMarker & {
  status: "acknowledged";
  version: 2;
};

export function resolveCurrentConsentEvidenceSnapshot(
  requested: ConsentSnapshot,
  current: ConsentSnapshot,
) {
  const normalizedCurrent = normalizeSharedConsentSnapshot(current);
  if (!normalizedCurrent) return null;
  return {
    snapshot: normalizedCurrent,
    requestedIsCurrent: sharedConsentSnapshotsEqual(requested, current),
  };
}

export function isConsentEvidenceSnapshotCurrent(
  expected: ConsentSnapshot,
  current: ConsentSnapshot,
) {
  return sharedConsentSnapshotsEqual(expected, current);
}

function markersEqual(
  left: ConsentEvidenceOutboxMarker,
  right: ConsentEvidenceOutboxMarker,
) {
  return (
    left.fingerprint === right.fingerprint &&
    left.revisionToken === right.revisionToken
  );
}

function readAcknowledgement(raw: string | null) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentEvidenceAcknowledgement>;
    if (
      parsed.version !== 2 ||
      parsed.status !== "acknowledged" ||
      typeof parsed.fingerprint !== "string" ||
      (parsed.revisionToken !== null &&
        typeof parsed.revisionToken !== "string")
    ) {
      return null;
    }
    return parsed as ConsentEvidenceAcknowledgement;
  } catch {
    // Plain legacy fingerprints were written before durable enqueue and are
    // therefore not proof of either a local outbox record or server receipt.
    return null;
  }
}

/**
 * Commit consent evidence using an ACK-aware outbox. The durable marker is
 * written only after confirmed HTTP success; before that, dedupe is allowed
 * solely against an actually present durable queue record.
 */
export function commitConsentEvidenceToDurableOutbox(options: {
  evidence: ConsentEvidence;
  fingerprintKey: string;
  revisionToken: string | null;
  storage: StorageAdapter;
  hasDurableEvidence: (marker: ConsentEvidenceOutboxMarker) => boolean;
  enqueue: (
    payload: ConsentEvidencePayload,
    marker: ConsentEvidenceOutboxMarker,
  ) => { durable: boolean };
}) {
  const marker = {
    fingerprint: options.evidence.fingerprint,
    revisionToken: options.revisionToken,
  };
  const acknowledged = readAcknowledgement(
    options.storage.get(options.fingerprintKey),
  );
  if (acknowledged && markersEqual(acknowledged, marker)) {
    return { acknowledged: true, deduplicated: true, durable: true };
  }

  if (options.hasDurableEvidence(marker)) {
    return { acknowledged: false, deduplicated: true, durable: true };
  }

  const enqueued = options.enqueue(options.evidence.payload, marker);
  if (!enqueued.durable) {
    return { acknowledged: false, deduplicated: false, durable: false };
  }

  return { acknowledged: false, deduplicated: false, durable: true };
}

export function acknowledgeConsentEvidenceDelivery(options: {
  currentMarker: ConsentEvidenceOutboxMarker | undefined;
  events: readonly { consentEvidence?: ConsentEvidenceOutboxMarker }[];
  fingerprintKey: string;
  storage: StorageAdapter;
  result: TransportResult;
}) {
  if (!options.result.ok || !options.currentMarker) return false;
  if (
    !options.events.some(
      (event) =>
        event.consentEvidence !== undefined &&
        markersEqual(event.consentEvidence, options.currentMarker!),
    )
  ) {
    return false;
  }

  const acknowledgement: ConsentEvidenceAcknowledgement = {
    ...options.currentMarker,
    status: "acknowledged",
    version: 2,
  };
  const serialized = JSON.stringify(acknowledgement);
  options.storage.set(options.fingerprintKey, serialized);
  const persisted = readAcknowledgement(
    options.storage.get(options.fingerprintKey),
  );
  return persisted !== null && markersEqual(persisted, options.currentMarker);
}

export function resolveConsentSignalSource(
  snapshot: ConsentSnapshot,
  fallbackSource?: string,
): string | undefined {
  if (snapshot.gpc) return "gpc";
  if (snapshot.dnt) return "dnt";
  if (snapshot.cmp?.source) return snapshot.cmp.source;
  return fallbackSource;
}

export function buildConsentEvidence(
  snapshot: ConsentSnapshot,
  fallbackSource?: string,
): ConsentEvidence | null {
  const normalized = normalizeSharedConsentSnapshot(snapshot);
  if (
    !normalized ||
    (normalized.state !== "granted" && normalized.state !== "denied")
  ) {
    return null;
  }
  const decision = normalized as ConsentSnapshot & {
    state: "granted" | "denied";
  };
  const effective =
    decision.gpc || decision.dnt
      ? { ...decision, state: "denied" as const }
      : decision;

  const resolvedSignalSource = resolveConsentSignalSource(
    effective,
    fallbackSource,
  );
  if (!resolvedSignalSource) return null;
  const signalSource = resolvedSignalSource.slice(0, 64);

  const payload: ConsentEvidencePayload = {
    state: effective.state,
    gpc: effective.gpc,
    dnt: effective.dnt,
    cmp: effective.cmp,
    signalSource,
  };

  return {
    fingerprint: JSON.stringify({
      state: effective.state,
      signalSource,
      gpc: effective.gpc,
      dnt: effective.dnt,
      cmpSource: effective.cmp?.source,
      tcString: effective.cmp?.tcString,
      uspString: effective.cmp?.uspString,
      gppString: effective.cmp?.gppString,
      gppSectionIds: effective.cmp?.gppSectionIds,
      purposes: effective.cmp?.purposes,
    }),
    payload,
  };
}
